import { ApolloError } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";

import {
  CreateProductInput,
  useCreateProductMutation,
  useDeleteProductsWithIdsMutation,
  useEditProductByIdMutation,
  useGetLatestProductLazyQuery,
  useGetProductWithIdLazyQuery,
} from "../../generated/graphql";
import { useAppSelector } from "../../hooks/usereduxHooks";
import { NumberFormatCustom } from "../../utils/CustomNumberInput";
import { sweetAlert } from "../../utils/sweetAlert";
import { ProductValidation } from "../../validation/productValidation";
import { useNavigate, useParams } from "react-router";
export const ModalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "calc(100vw - 32px)",
  maxWidth: "850px",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  backgroundColor: "background.paper",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
  borderRadius: "12px",
  p: { xs: 2, sm: 3 },
  outline: "none",
};

interface ExtendCreateProductInput extends CreateProductInput {
  id?: number;
}

export interface AddProductModalProps {
  isModal?: boolean;
  onSuccess?: (createdProduct: any) => void;
  onClose?: () => void;
  initialItemCode?: string;
}

export default function AddProductModal({
  isModal = false,
  onSuccess,
  onClose,
  initialItemCode,
}: AddProductModalProps = {}) {
  const { editCode: routeEditCode } = useParams();
  const editCode = initialItemCode || routeEditCode;

  const navigate = useNavigate();
  const {
    user: { id: userId },
  } = useAppSelector((store) => store.User);

  const itemCodeRef = useRef<HTMLInputElement>();

  const initialProduct = useMemo(
    () => ({
      userId,
      itemCode: editCode ? String(editCode) : "",
      name: "",
      unitPurchasePrice: 0,
      amount: 0,
      pcsOrBundle: 0,
      meterOrSuit: 0,
      sumUpMeterOrSuitBalance: 0,
      sumUpPcsOrBundleBalance: 0,
      quality: undefined,
    }),
    [editCode, userId],
  );

  const [createProduct, { loading }] = useCreateProductMutation({
    onCompleted: (data) => {
      const created = data?.createProduct || values;
      if (isModal && onSuccess) {
        sweetAlert("Item created successfully", "success").then(() => {
          onSuccess(created);
          onClose?.();
        });
        return;
      }

      resetForm();
      navigate("/items/add-item");

      return sweetAlert("Create Successfully", "success").then(async () => {
        await getLatestProduct();
        setTimeout(() => {
          itemCodeRef?.current?.focus();
        }, 500);
      });
    },
    onError: (err: ApolloError) => {
      return sweetAlert(err.message, "error");
    },
  });

  const [editProductMutate, { loading: productLoading }] =
    useEditProductByIdMutation({
      onCompleted: (data) => {
        const updated = data?.editProductById || values;
        if (isModal && onSuccess) {
          sweetAlert("Product Updated", "success").then(() => {
            onSuccess(updated);
            onClose?.();
          });
          return;
        }

        resetForm();
        navigate("/items/add-item");

        return sweetAlert("Product Updated", "success").then(async () => {
          await getLatestProduct();
          setTimeout(() => {
            itemCodeRef?.current?.focus();
          }, 500);
        });
      },
      onError: (err: ApolloError) => {
        return sweetAlert(err.message, "error");
      },
    });

  const [getProductFromItemCode] = useGetProductWithIdLazyQuery({});

  const formik = useFormik({
    enableReinitialize: true,
    onSubmit: async (values: ExtendCreateProductInput) => {
      try {
        if (!values.id) {
          delete values.sumUpMeterOrSuitBalance;
          delete values.sumUpPcsOrBundleBalance;
          await createProduct({
            variables: {
              createProductInput: {
                ...values,
                itemCode: values.itemCode.trim(),

                unitPurchasePrice: parseFloat(`${values.unitPurchasePrice}`),
                pcsOrBundle: parseFloat(`${values.pcsOrBundle}`),
                meterOrSuit: parseFloat(`${values.meterOrSuit}`),
                amount: Number(
                  (values.unitPurchasePrice * values.meterOrSuit).toFixed(2),
                ),
              },
            },
          });
        } else {
          await editProductMutate({
            variables: {
              editProductWithInitialInput: {
                id: values.id,
                itemCode: values.itemCode.trim(),
                name: values.name,
                unitPurchasePrice: parseFloat(`${values.unitPurchasePrice}`),
                pcsOrBundle: parseFloat(`${values.pcsOrBundle}`),
                meterOrSuit: parseFloat(`${values.meterOrSuit}`),
                sumUpMeterOrSuitBalance: parseFloat(`${values.meterOrSuit}`),
                sumUpPcsOrBundleBalance: parseFloat(`${values.pcsOrBundle}`),
                amount: Number(
                  (values.unitPurchasePrice * values.meterOrSuit).toFixed(2),
                ),
              },
            },
          });
        }
      } catch (err) {}
    },
    initialValues: {
      ...initialProduct,
    },
    validationSchema: ProductValidation,
  });

  const [deleteProductWithIdsMutate] = useDeleteProductsWithIdsMutation();

  const {
    handleBlur,
    resetForm,
    errors,
    handleChange,
    touched,
    setValues,
    setFieldValue,
    values,
    handleSubmit,
  } = formik;

  const onItemCodeBlur = useCallback(
    async (codeToFind?: string) => {
      const targetCode = (codeToFind ?? values.itemCode)?.trim();
      if (!targetCode) return;
      const product = await getProductFromItemCode({
        variables: {
          id: targetCode,
        },
        fetchPolicy: "no-cache",
      });
      if (product.data?.getProductWithId) {
        setValues({
          ...product.data?.getProductWithId,
          userId,
          pcsOrBundle: product!.data!.getProductWithId!
            .sumUpPcsOrBundleBalance! as number,
          meterOrSuit: product!.data!.getProductWithId!
            .sumUpMeterOrSuitBalance! as number,
        });
      } else if (!editCode) {
        setFieldValue("id", undefined);
      }
    },
    [editCode, getProductFromItemCode, setFieldValue, setValues, userId, values.itemCode],
  );

  const [getQuery] = useGetLatestProductLazyQuery();

  const getLatestProduct = useCallback(async () => {
    if (editCode) {
      await onItemCodeBlur(editCode);
    } else {
      const { data } = await getQuery({
        fetchPolicy: "no-cache",
      });

      let itemCode = "001";

      if (data?.getLatestProduct.itemCode) {
        let numberPart = !isNaN(
          Number(data?.getLatestProduct?.itemCode.replace(/^\D+/g, "")),
        )
          ? Number(data?.getLatestProduct?.itemCode.replace(/^\D+/g, "")) + 1
          : "001";
        let stringPart = data?.getLatestProduct.itemCode.replace(/[\d\s]/g, "");
        itemCode = `${stringPart} ${numberPart.toString().padStart(3, "0")}`;
      }
      setValues({
        ...initialProduct,
        itemCode: initialItemCode || itemCode,
      });
    }
  }, [editCode, getQuery, initialItemCode, initialProduct, onItemCodeBlur, setValues]);

  useEffect(() => {
    getLatestProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCode, initialItemCode]);
  return (
    <Grid
      item
      xs={12}
      md={12}
      sm={12}
      sx={{
        maxWidth: isModal ? "100%" : "960px",
        mx: isModal ? 0 : "auto",
        width: "100%",
      }}
    >
      {!isModal && (
        <Box display="flex" alignItems="center" justifyContent={"space-between"}>
          <Box
            display="flex"
            alignItems="flex-start"
            flexDirection={"column"}
            justifyContent={"flex-start"}
            marginBottom={{ xs: "20px", md: "0px" }}
          >
            <Typography variant="h3" component="h3" gutterBottom>
              Items
            </Typography>
            <Typography variant="subtitle2">Add Item</Typography>
          </Box>
        </Box>
      )}
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <FormikProvider value={formik}>
          <Grid
            rowSpacing={0}
            columnSpacing={{ xs: 2, sm: 2, md: 3 }}
            container
            alignItems={"center"}
          >
            <Grid item md={6} sm={12} xs={12}>
              <TextField
                size="small"
                inputProps={{
                  maxLength: 15,
                  style: { textTransform: "uppercase" },
                }}
                margin="normal"
                required
                fullWidth
                placeholder="Product Code"
                label="Product Code"
                id="itemCode"
                value={values.itemCode}
                aria-required
                name="itemCode"
                onChange={handleChange}
                onBlur={(e) => {
                  handleBlur(e);
                  onItemCodeBlur();
                }}
                autoComplete="itemCode"
                error={Boolean(touched.itemCode && errors.itemCode)}
              />
              <FormHelperText sx={{ color: "error.main", height: "20px" }}>
                {errors["itemCode"] &&
                  (touched.itemCode ? errors.itemCode : "")}
              </FormHelperText>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <TextField
                size="small"
                inputProps={{
                  maxLength: 35,
                  style: { textTransform: "uppercase" },
                }}
                margin="normal"
                required
                fullWidth
                inputRef={itemCodeRef}
                placeholder="Product Name"
                label="Product Name"
                id="name"
                value={values.name}
                aria-required
                name="name"
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="name"
                error={Boolean(touched.name && errors.name)}
              />
              <FormHelperText sx={{ color: "error.main", height: "20px" }}>
                {errors["name"] && (touched.name ? errors.name : "")}
              </FormHelperText>
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <TextField
                size="small"
                margin="normal"
                required
                fullWidth
                type={"string"}
                placeholder="Pcs / Bundle"
                label="Pcs / Bundle"
                id="pcsOrBundle"
                value={values.pcsOrBundle.toString()}
                aria-required
                name="pcsOrBundle"
                InputProps={{
                  inputComponent: NumberFormatCustom as any,

                  inputProps: {
                    max: 99999,
                    min: 0,
                  },
                }}
                disabled={false}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="pcsOrBundle"
                error={Boolean(touched.pcsOrBundle && errors.pcsOrBundle)}
              />
              <FormHelperText sx={{ color: "error.main", height: "20px" }}>
                {errors["pcsOrBundle"] &&
                  (touched.pcsOrBundle ? errors.pcsOrBundle : "")}
              </FormHelperText>
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <TextField
                size="small"
                margin="normal"
                required
                fullWidth
                type={"string"}
                placeholder="Meter / Suit"
                label="Meter / Suit"
                id="meterOrSuit"
                value={values.meterOrSuit.toString()}
                aria-required
                InputProps={{
                  inputComponent: NumberFormatCustom as any,
                  inputProps: {
                    max: 9999999,
                    min: 0,
                  },
                }}
                name="meterOrSuit"
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="meterOrSuit"
                disabled={false}
                error={Boolean(touched.meterOrSuit && errors.meterOrSuit)}
              />
              <FormHelperText sx={{ color: "error.main", height: "20px" }}>
                {errors["meterOrSuit"] &&
                  (touched.meterOrSuit ? errors.meterOrSuit : "")}
              </FormHelperText>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <TextField
                size="small"
                margin="normal"
                required
                fullWidth
                placeholder="Unit Purchase"
                label="Unit Purchase"
                id="unitPurchasePrice"
                value={values.unitPurchasePrice.toString()}
                aria-required
                InputProps={{
                  inputComponent: NumberFormatCustom as any,

                  inputProps: {
                    max: 9999999,
                    min: 0,
                  },
                }}
                name="unitPurchasePrice"
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="unitPurchasePrice"
                error={Boolean(
                  touched.unitPurchasePrice && errors.unitPurchasePrice,
                )}
              />
              <FormHelperText sx={{ color: "error.main", height: "20px" }}>
                {errors["unitPurchasePrice"] &&
                  (touched.unitPurchasePrice ? errors.unitPurchasePrice : "")}
              </FormHelperText>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <TextField
                size="small"
                margin="normal"
                required
                fullWidth
                placeholder="Amount"
                label="Amount"
                id="amount"
                value={(values.unitPurchasePrice * values.meterOrSuit).toFixed(
                  2,
                )}
                aria-required
                InputProps={{
                  inputComponent: NumberFormatCustom as any,

                  inputProps: {
                    max: 999999999999,
                    min: 0,
                  },
                }}
                name="amount"
                disabled
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="amount"
              />
              {errors["amount"] && (touched.amount ? errors.amount : "")}
            </Grid>
          </Grid>
        {isModal ? (
          <Grid
            container
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 2, mb: 1 }}
          >
            {onClose && (
              <Grid item xs={6} sm={4}>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={onClose}
                >
                  Cancel
                </LoadingButton>
              </Grid>
            )}
            <Grid item xs={onClose ? 6 : 12} sm={4}>
              <LoadingButton
                loading={loading || productLoading}
                type="submit"
                fullWidth
                variant="contained"
              >
                {values.id ? "Update Product" : "Create Product"}
              </LoadingButton>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3} justifyContent={"center"}>
            <Grid item md={!values.id ? 12 : 4} sm={12}>
              <LoadingButton
                loading={loading || productLoading}
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                {values.id ? "Update Product" : "Create Product"}
              </LoadingButton>
            </Grid>
            {values.id ? (
              <>
                <Grid item md={4} sm={12}>
                  <LoadingButton
                    loading={loading || productLoading}
                    type="button"
                    onClick={async () => {
                      const id = values.id as number;
                      Swal.fire({
                        title: "Do you really want to Delete?",
                        showDenyButton: true,
                        showCancelButton: true,
                        confirmButtonText: "Delete",
                        denyButtonText: `Don't Delete`,
                      }).then(async (result) => {
                        /* Read more about isConfirmed, isDenied below */
                        if (result.isConfirmed) {
                          await deleteProductWithIdsMutate({
                            variables: {
                              deletewithids: {
                                id: [id],
                              },
                            },
                          });
                          setValues({
                            ...initialProduct,
                          });
                          navigate("/items/add-item");

                          return sweetAlert("Deleted", "success").then(
                            async () => {
                              await getLatestProduct();
                              setTimeout(() => {
                                itemCodeRef?.current?.focus();
                              }, 500);
                            },
                          );
                        }
                      });
                    }}
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Delete Product
                  </LoadingButton>
                </Grid>

                <Grid item md={4} sm={12}>
                  <LoadingButton
                    loading={loading || productLoading}
                    onClick={() => {
                      navigate("/items/add-item");

                      setValues({
                        ...initialProduct,
                      });

                      getLatestProduct();
                    }}
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Clear information
                  </LoadingButton>
                </Grid>
              </>
            ) : null}
          </Grid>
        )}
        </FormikProvider>
      </Box>
    </Grid>
  );
}
